import React, { useEffect, useMemo, useState, useRef } from "react"
import * as url from "../../helpers/url_helper"

import {
    Alert,
    Button,
    Card,
    CardBody,
    Col,
    Form,
    FormFeedback,
    FormGroup,
    Label,
    Row,
} from "reactstrap"

import { useFormik } from "formik"
import {
    convertFileToBase64,
    formatBytes,
    validateImageFile,
} from "helpers/file_helper"
import { validateBeforeConfirm } from "helpers/validation_helper"
import Dropzone from "react-dropzone"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import {
    addNewMerchantGroup,
    getMerchantGroupDetail,
    uploadBase64FileAction,
} from "store/actions"
import {
    clearMerchantGroupDetail,
    clearMerchantGroupCommunications,
    clearMerchantGroupContacts,
} from "store/merchant-group/actions"
import { getCitiesAction as onGetCities } from "store/actions"
import * as Yup from "yup"
import ConfirmButton from "components/Common/ConfirmButton"
import { uploadBase64File } from "../../helpers/backend_helper"
import TextInput from "components/Common/TextInput"
import TextArea from "components/Common/TextArea"
import SelectInput from "components/Common/SelectInput"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import AccessControlWrapper from "components/Common/PermissionButton"

export default function MerchantGroupForm({
    page,
    match,
    history,
    onMerchantGroupCreated,
    onTabCompletion,
    navigateToNextTab,
    activeTab,
    onFormRef,
    onReadOnlyChange,
    onFormChange,
}) {
    const [selectedFiles, setselectedFiles] = useState([])
    const [logoCleared, setLogoCleared] = useState(false)
    const [readOnly, setReadOnly] = useState(page === "detail")
    const [isUploading, setIsUploading] = useState(false)
    const [merchantGroupID, setMerchantGroupID] = useState(null)
    const previousMerchantGroupsLength = useRef(0)
    const previousError = useRef(null)

    const detailID = match?.params?.id

    const dispatch = useDispatch()
    const { data, appInfo, cities } = useSelector(state => ({
        data: state.merchantGroup,
        appInfo: state.appInfo.appInfo,
        cities: state.appInfo.cities || [],
    }))

    const merchantGroup =
        page === "add"
            ? null
            : data?.merchantGroupDetail && Array.isArray(data.merchantGroupDetail)
                ? data.merchantGroupDetail.find(mg => mg.merchantGroupID === detailID)
                : data?.merchantGroupDetail

    //Merchant Group Detail Data
    useEffect(() => {
        if (page === "detail" && !merchantGroup) {
            // Check for updated parameter in URL to bust cache
            const urlParams = new URLSearchParams(window.location.search)
            const isUpdated = urlParams.get('updated') === 'true'

            const options = {
                headers: {
                    "input-parameters": { merchantGroupID: detailID },
                },
            }

            // Add timestamp as URL query parameter for cache busting if updated parameter is present
            if (isUpdated) {
                options.params = { updated: Date.now() }
            }

            dispatch(getMerchantGroupDetail(options))
        } else if (page === "add") {
            // Clear any existing merchant group data when adding new merchant group
            dispatch(clearMerchantGroupDetail())
            dispatch(clearMerchantGroupCommunications())
            dispatch(clearMerchantGroupContacts())
        }
    }, [dispatch, page, detailID, merchantGroup])

    // Clear all merchant group data when component unmounts or detailID changes
    useEffect(() => {
        return () => {
            dispatch(clearMerchantGroupDetail())
            dispatch(clearMerchantGroupCommunications())
            dispatch(clearMerchantGroupContacts())
        }
    }, [dispatch, detailID])

    // City dropdown styles are now handled by SelectInput component

    useEffect(() => {
        if (!cities.length && appInfo?.appCountryID) {
            dispatch(
                onGetCities({
                    headers: {
                        "input-parameters": { countryIDs: [appInfo.appCountryID] },
                    },
                })
            )
        }
    }, [dispatch, cities.length, appInfo?.appCountryID])

    useEffect(() => {
        if (page === "detail" && readOnly === true && merchantGroup?.logo && !logoCleared) {
            const currentPreview = selectedFiles[0]?.preview || ""
            const isLocalBlobUrl = currentPreview.startsWith("blob:")

            if (isLocalBlobUrl) {
                return
            }

            const file = {
                preview: merchantGroup.logo,
            }
            setselectedFiles([file])
        }
    }, [merchantGroup, page, readOnly, logoCleared])

    const citiesOptions = useMemo(() => {
        return cities.map(city => ({
            value: city.cityID,
            label: city.name,
        }))
    }, [cities])

    async function handleAcceptedFiles(files) {
        if (files.length > 0) {
            const file = files[0]

            const base64Img = await convertFileToBase64(file)

            try {
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                    formattedSize: formatBytes(file.size),
                    forExistingResource: false,
                    base64encodedFileByteArray: base64Img,
                    resourceType: "EUROPA_MERCHANT_GROUP_LOGO",
                })
                setselectedFiles([file])
                setLogoCleared(false) // Reset cleared flag when uploading new image
                validation.setFieldValue("logo", file)
            } catch (error) {
                console.error("Error converting to base64:", error)
            }
        }
    }

    // Form validation
    const validation = useFormik({
        enableReinitialize: true,

        initialValues: {
            legalEntityName: merchantGroup?.legalEntityName || "",
            displayName: merchantGroup?.displayName || "",
            legalEntityAddress: merchantGroup?.legalEntityAddress || "",
            city: merchantGroup?.city
                ? {
                    value: merchantGroup.city.cityID,
                    label: merchantGroup.city.name,
                }
                : null,
            briefInfo: merchantGroup?.briefInfo || "",
            description: merchantGroup?.description || "",
            taxRegnNum: merchantGroup?.taxRegnNum || "",
            merchantGroupID: merchantGroup?.merchantGroupID || "",
            logo: merchantGroup?.logo
                ? {
                    preview: merchantGroup.logo,
                }
                : "",
        },
        validationSchema: Yup.object({
            legalEntityName: Yup.string()
                .required("Please Enter Legal Entity Name")
                .max(255, "Legal Entity Name cannot be greater than 255"),

            displayName: Yup.string()
                .required("Please Enter Display Name")
                .max(255, "Display Name cannot be greater than 255"),

            legalEntityAddress: Yup.string()
                .required("Please Enter Legal Entity Address")
                .max(255, "Legal Entity Address cannot be greater than 255"),

            description: Yup.string().max(
                255,
                "Description cannot be greater than 255"
            ),

            briefInfo: Yup.string().max(255, "Brief Info cannot be greater than 255"),

            taxRegnNum: Yup.string().required().max(255, "Tax Registration Number cannot be greater than 255"),

            city: Yup.object().nullable().required("Please Select City"),

            logo: Yup.mixed()
                .nullable()
                .when([], {
                    is: () => page === "add",
                    then: schema => schema.optional("Please Upload Merchant Group Logo"),
                    otherwise: schema => schema,
                })
                .test(
                    "merchantGroupImage",
                    "Invalid Merchant Group image",
                    async function (value) {
                        if (!value && page !== "add") {
                            return true
                        }

                        if (!value) {
                            return true
                        }

                        try {
                            await validateImageFile(value, "merchantGroup", 550, 550, 1) // 550x550, 1MB
                            return true
                        } catch (error) {
                            return this.createError({ message: error.message })
                        }
                    }
                ),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })

    // Ref to hold current validation object to prevent rerenders
    const validationRef = useRef(validation)

    // Update validation ref whenever validation changes
    useEffect(() => {
        validationRef.current = validation
    }, [validation])

    // Expose form methods to parent component
    useEffect(() => {
        if (onFormRef && page === "add") {
            onFormRef({
                handleSubmit: async () => {
                    const isValid = await validateBeforeConfirm(validationRef.current)
                    if (isValid) {
                        handleConfirmedSubmit()
                    }
                },
                submitForm: () =>
                    new Promise(async resolve => {
                        const isValid = await validateBeforeConfirm(validationRef.current)
                        if (isValid) {
                            handleConfirmedSubmit()
                        }
                        resolve()
                    }),
            })
        }
    }, [onFormRef, page]) // Removed validation from dependencies

    // Notify parent of form changes for breadcrumb secondary info
    useEffect(() => {
        if (onFormChange) {
            onFormChange({
                legalEntityName: validation.values.legalEntityName || "",
                legalEntityAddress: validation.values.legalEntityAddress || ""
            })
        }
    }, [validation.values.legalEntityName, validation.values.legalEntityAddress])

    // Function to handle the actual form submission after confirmation
    const handleConfirmedSubmit = async () => {
        setIsUploading(true)

        try {
            const values = validationRef.current.values
            const logoValue = values.logo

            let logoData = ""

            // Handle logo upload logic
            if (logoValue && logoValue.base64encodedFileByteArray) {
                // User uploaded a new image
                try {
                    // Call the API directly instead of through dispatch
                    const uploadResponse = await uploadBase64File(logoValue, {})

                    if (uploadResponse && uploadResponse.fileName) {
                        logoData = uploadResponse.fileName
                    } else {
                        throw new Error("Failed to upload logo - no filename returned")
                    }
                } catch (uploadError) {
                    console.error("Logo upload failed:", uploadError)
                    // You might want to show an error message to the user here
                    setIsUploading(false)
                    return
                }
            } else if (logoValue && logoValue.preview && !logoValue.base64encodedFileByteArray) {
                // User has existing image and didn't change it - extract filename from URL
                const url = logoValue.preview
                const urlParts = url.split('/')
                logoData = urlParts[urlParts.length - 1] // Get the last part of the URL (filename)
            } else if (logoValue === "") {
                // User explicitly cleared the logo
                logoData = ""
            }

            const payload = {
                legalEntityName: values.legalEntityName,
                displayName: values.displayName,
                legalEntityAddress: values.legalEntityAddress,
                cityID: values.city?.value || "",
                briefInfo: values.briefInfo || "",
                description: values.description || "",
                taxRegnNum: values.taxRegnNum || "",
                actionType: page === "add" && !merchantGroupID ? "create" : "update",
                logo: logoData || "",
            }

            if (page === "detail" || merchantGroupID) {
                payload.merchantGroupID = values.merchantGroupID || merchantGroupID
            }

            const options = {
                // encDecAlgo: "BASE64",
            }

            // Now dispatch the create/update action with the uploaded logo filename
            dispatch(
                addNewMerchantGroup(payload, options, page === "add" ? null : history)
            )

            // For detail/edit mode, handle success immediately since we don't need to monitor state changes
            if (page === "detail") {

                // Set readonly mode after update in detail mode
                setReadOnly(true)
                if (onReadOnlyChange) {
                    onReadOnlyChange(true)
                }

                // Clear image state if it was removed in the form
                if (values.logo === "") {
                    setselectedFiles([]);
                    setLogoCleared(true);
                }

                // Add updated=true parameter to URL for cache-busting on next load
                const currentUrl = new URL(window.location)
                currentUrl.searchParams.set('updated', 'true')

                history.replace(currentUrl.pathname + currentUrl.search)
            }

            // For add mode, we let the useEffect hook handle success/error monitoring
        } catch (error) {
            console.error("Form submission error:", error)
            setIsUploading(false)
        } finally {
            // For detail mode, always reset isUploading
            if (page === "detail") {
                setIsUploading(false)
            }
            // For add mode, reset isUploading after a delay to ensure error handling completes
            else if (page === "add") {
                setTimeout(() => {
                    setIsUploading(false)
                }, 2000) // Reset after 2 seconds as fallback
            }
        }
    }

    const clearLogo = () => {
        setselectedFiles([])
        setLogoCleared(true)
        validation.setFieldValue("logo", "")
    }

    // City selection is now handled by SelectInput component

    const { merchantGroupError, merchantGroups, creationSuccess } = useSelector(
        state => ({
            merchantGroupError:
                state.merchantGroup.error?.message ||
                state.merchantGroup.error?.description,
            merchantGroups: state.merchantGroup.merchantGroups,
            creationSuccess: state.merchantGroup.creationSuccess,
        })
    )

    // Monitor Redux state changes to handle success/error after form submission
    useEffect(() => {
        // Only monitor in add mode and when form is uploading
        if (page !== "add" || !isUploading) return

        // Check for successful creation using creationSuccess
        if (
            creationSuccess &&
            creationSuccess !== previousMerchantGroupsLength.current
        ) {
            // Set readonly mode after successful creation
            setReadOnly(true)
            setMerchantGroupID(
                creationSuccess.merchantGroupID || Date.now().toString()
            )

            // Notify parent about readonly state change
            if (onReadOnlyChange) {
                onReadOnlyChange(true)
            }

            if (onMerchantGroupCreated) {
                onMerchantGroupCreated(creationSuccess.merchantGroupID)
            }

            setIsUploading(false)
            previousMerchantGroupsLength.current = creationSuccess
        }

        // Check for errors (error state changed and is different from previous)
        else if (
            merchantGroupError &&
            merchantGroupError !== previousError.current
        ) {
            setIsUploading(false)
            previousError.current = merchantGroupError
        }
    }, [
        creationSuccess,
        merchantGroupError,
        isUploading,
        page,
        onMerchantGroupCreated,
        onReadOnlyChange,
        navigateToNextTab,
    ])

    // Initialize previous values
    useEffect(() => {
        previousMerchantGroupsLength.current = creationSuccess
        previousError.current = merchantGroupError
    }, [])

    return (
        <>
            {((page === "detail" && readOnly) || (page === "add" && readOnly)) && (
                <div className="mb-3 d-flex justify-content-end">
                    <AccessControlWrapper
                        contextUri={url.MANAGE_MERCHANT_GROUP}
                        actionType="post"
                    >
                        <CButton.Primary
                            text="Edit Merchant Group"
                            onClick={() => {
                                setReadOnly(false)
                                setLogoCleared(false) // Reset logo cleared flag when entering edit mode
                                if (onReadOnlyChange) {
                                    onReadOnlyChange(false)
                                }
                            }}
                        />
                    </AccessControlWrapper>
                </div>
            )}

            {merchantGroupError && (
                <Alert color="danger" className="mb-3">
                    {merchantGroupError}
                </Alert>
            )}

            <Card>
                <CardBody>
                    <Form
                        className="needs-validation"
                        onSubmit={e => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                        }}
                    >
                        <Row>
                            {/* Legal Entity Name */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"legalEntityName"}
                                    placeholder={"Enter Legal Entity Name"}
                                    label={"Legal Entity Name"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* Display Name */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"displayName"}
                                    placeholder="Enter Display Name"
                                    label={"Display Name"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* Legal Entity Address */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"legalEntityAddress"}
                                    placeholder="Enter Legal Entity Address"
                                    label={"Legal Entity Address"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>

                            {/* City ID */}
                            <Col md="6">
                                <SelectInput
                                    name="city"
                                    label="City"
                                    placeholder="Select City..."
                                    options={citiesOptions}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>
                            {/* Tax Registration Number */}
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name={"taxRegnNum"}
                                    placeholder="Enter Tax Registration Number"
                                    label={"Tax Registration Number"}
                                    helperText={"Max 255 chars allowed"}
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly}
                                    required={true}
                                />
                            </Col>
                        </Row>

                        {/* text area's row */}
                        <Row>
                            {/* Brief Info */}
                            <Col md="6">
                                <TextArea
                                    label={"Brief Info"}
                                    placeholder="Enter Brief Info"
                                    helperText={"Max 255 chars allowed"}
                                    name={"briefInfo"}
                                    validation={validation}
                                    required={false}
                                    readOnly={readOnly}
                                    maxLength={255}
                                />
                            </Col>

                            <Col md="6">
                                <TextArea
                                    label={"Description"}
                                    name={"description"}
                                    placeholder="Enter Description"
                                    type="textarea"
                                    readOnly={readOnly}
                                    validation={validation}
                                    required={false}
                                    maxLength={"255"}
                                    helperText={"Max 255 chars allowed"}
                                />
                            </Col>
                        </Row>

                        <Row>
                            {/* Logo */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>Logo{page === "add" && !readOnly ? "" : ""}</Label>

                                    {(selectedFiles.length === 0 || validation.errors.logo) && readOnly === false ? (
                                        <>
                                            <Dropzone
                                                multiple={false}
                                                accept={{
                                                    "image/*": [".png", ".jpeg", ".jpg"],
                                                }}
                                                onDrop={acceptedFiles => {
                                                    handleAcceptedFiles(acceptedFiles)
                                                }}
                                            >
                                                {({ getRootProps, getInputProps }) => (
                                                    <div className="dropzone">
                                                        <div
                                                            className="dz-message needsclick mt-2"
                                                            {...getRootProps()}
                                                        >
                                                            <input {...getInputProps()} />
                                                            <div className="mb-3">
                                                                <i className="display-4 text-muted bx bxs-cloud-upload" />
                                                            </div>
                                                            <h4>
                                                                Drop Merchant Group image here or click to
                                                                upload.
                                                            </h4>
                                                        </div>
                                                    </div>
                                                )}
                                            </Dropzone>
                                        </>
                                    ) : (
                                        readOnly === true && selectedFiles.length === 0 && <p className="fs-5 text-danger">No Logo found</p>
                                    )}

                                    {selectedFiles.length > 0 && !validation.errors.logo ? (
                                        <div className="dropzone-previews mt-3" id="file-previews">
                                            {selectedFiles.map((f, i) => {
                                                return (
                                                    <Card
                                                        className="d-inline-block mt-1 mb-2 position-relative shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                        key={i + "-file"}
                                                    >
                                                        <div className="p-2">
                                                            {readOnly ? null : (
                                                                <IconButton
                                                                    icon="bx bxs-x-circle"
                                                                    variant="rounded-circle"
                                                                    className="position-absolute right-0 top-0 start-100 translate-middle"
                                                                    style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                                    onClick={clearLogo}
                                                                    title="Clear Logo"
                                                                />
                                                            )}

                                                            <div>
                                                                <div className="text-center mb-2">
                                                                    <img
                                                                        data-dz-thumbnail=""
                                                                        height="200"
                                                                        className="rounded bg-light"
                                                                        alt={f.name || "Logo"}
                                                                        src={f?.preview}
                                                                    />
                                                                </div>
                                                                {f.name && f.formattedSize ? (
                                                                    <div className="text-center">
                                                                        <Link
                                                                            to="#"
                                                                            className="text-muted font-weight-bold"
                                                                        >
                                                                            {f.name}
                                                                        </Link>
                                                                        <p className="mb-0">
                                                                            <strong>{f.formattedSize}</strong>
                                                                        </p>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )
                                            })}
                                        </div>
                                    ) : null}

                                    {validation.errors.logo ? (
                                        <small
                                            className={`invalid-feedback d-inline-block align-top ${validation.touched.logo && validation.errors.logo
                                                ? "w-50"
                                                : "w-100"
                                                }`}
                                        >
                                            {validation.errors.logo}
                                        </small>
                                    ) : null}

                                    {((selectedFiles.length === 0 && !readOnly) ||
                                        validation.errors.logo) && (
                                            <span className="text-muted text-end d-inline-block w-100">
                                                jpg & png are allowed upto 1mb max (550x550)
                                            </span>
                                        )}
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Submit Button - Only show for detail/edit mode or add mode after creation */}
                        {!readOnly && (page === "detail" || (page === "add" && merchantGroupID)) && (
                            <Row>
                                <Col md="12" className="text-end">
                                    <CButton.Secondary
                                        text="Cancel"
                                        className="me-2"
                                        onClick={() => {
                                            setReadOnly(true)
                                            setLogoCleared(false) // Reset logo cleared flag
                                            if (onReadOnlyChange) {
                                                onReadOnlyChange(true)
                                            }
                                            
                                            // In add mode with created merchant group, keep current form values
                                            // In edit mode, reset to original merchant group values
                                            if (page === "add" && merchantGroupID) {
                                                // For add mode after creation, just set readonly without resetting form
                                                // The form already has the values that were used to create the merchant group
                                                return
                                            }
                                            
                                            // Reset form to original values (for edit mode)
                                            validation.resetForm()
                                            // Reset logo file selection
                                            if (page === "detail" && merchantGroup?.logo) {
                                                const file = {
                                                    preview: merchantGroup?.logo || "",
                                                }
                                                setselectedFiles([file])
                                            } else {
                                                setselectedFiles([])
                                            }
                                        }}
                                        disabled={isUploading}
                                    />
                                    <ConfirmButton
                                        validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                        onConfirm={handleConfirmedSubmit}
                                        message="Are you sure you want to update this Merchant Group? This action will modify the existing Merchant Group information."
                                        color="primary"
                                        disabled={isUploading}
                                    >
                                        {isUploading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Uploading...
                                            </>
                                        ) : (
                                            "Update Merchant Group"
                                        )}
                                    </ConfirmButton>
                                </Col>
                            </Row>
                        )}

                        {/* Create Button - Only show for add mode when not readonly */}
                        {!readOnly && !merchantGroupID && page === "add" && (
                            <Row>
                                <Col md="12" className="text-end">
                                    <ConfirmButton
                                        validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                        onConfirm={handleConfirmedSubmit}
                                        message="Are you sure you want to create this Merchant Group?"
                                        color="primary"
                                        disabled={isUploading}
                                        contextUri={url.MANAGE_MERCHANT_GROUP}
                                        actionType="post"
                                    >
                                        {isUploading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Creating...
                                            </>
                                        ) : (
                                            "Create Merchant Group"
                                        )}
                                    </ConfirmButton>
                                </Col>
                            </Row>
                        )}
                    </Form>
                </CardBody>
            </Card>
        </>
    )
}
